import React, { useState, useMemo } from 'react';

// --- Internal Component: Acknowledgment Modal ---
const DPDPAcknowledgeModal = ({ onClose, onAcknowledge }) => {
    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [employeeId, setEmployeeId] = useState('');
    const [signature, setSignature] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(false);
    const today = useMemo(() => new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }), []);

    const handleSignatureChange = (e) => {
        setSignature(e.target.value);
    };

    const handleFinalAcknowledge = () => {
        if (name && employeeId && signature && isConfirmed) {
            onAcknowledge({ name, employeeId, signature, date: today });
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center p-4 z-50 transition-opacity duration-300">
            <div className="bg-white rounded-xl shadow-4xl w-full max-w-lg overflow-hidden transform transition-all scale-100 border-t-8 border-cyan-600">
                
                {/* Modal Header */}
                <div className="p-6 bg-cyan-50 flex justify-between items-center">
                    <h3 className="text-xl font-poppins font-bold text-gray-800">
                        Data Protection Policy Acknowledgment (Step {step} of 3)
                    </h3>
                    <button onClick={onClose} className="text-gray-600 hover:text-gray-900 transition">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-6">
                    {step === 1 && (
                        <div className="space-y-4">
                            <h4 className="font-poppins font-semibold text-cyan-700">Declaration of Understanding</h4>
                            <p className="text-sm text-gray-600 leading-relaxed bg-cyan-100 p-4 rounded-lg border border-cyan-200">
                                I affirm that I have accessed, read, and fully understand the company's Data Protection and DPDP Compliance Policy, which adheres to the <span className="font-semibold">Digital Personal Data Protection Act, 2023, Government of India</span>. I acknowledge my responsibility to protect personal data as defined herein.
                            </p>
                            <label className="flex items-center space-x-2 text-sm text-gray-700 pt-2">
                                <input 
                                    type="checkbox" 
                                    checked={isConfirmed}
                                    onChange={(e) => setIsConfirmed(e.target.checked)}
                                    className="h-5 w-5 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500 focus:border-cyan-500"
                                />
                                <span>I affirm that I understand and consent to the responsibilities outlined in this policy.</span>
                            </label>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4 font-roboto">
                            <h4 className="font-poppins font-semibold text-cyan-700">Identification Details</h4>
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Legal Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-cyan-500 focus:border-cyan-500"
                                    placeholder="Enter your full name"
                                />
                            </div>
                            <div>
                                <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700">Employee ID</label>
                                <input
                                    type="text"
                                    id="employeeId"
                                    value={employeeId}
                                    onChange={(e) => setEmployeeId(e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-cyan-500 focus:border-cyan-500"
                                    placeholder="e.g., EMP12345"
                                />
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4">
                            <h4 className="font-poppins font-semibold text-cyan-700">Digital Signature</h4>
                            <div>
                                <label htmlFor="signature" className="block text-sm font-medium text-gray-700">Type Your Full Signature (Full Name)</label>
                                <input
                                    type="text"
                                    id="signature"
                                    value={signature}
                                    onChange={handleSignatureChange}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-lg p-4 text-xl tracking-wide focus:ring-cyan-500 focus:border-cyan-500 font-poppins font-semibold"
                                    placeholder="e.g., Varun Sharma"
                                />
                                <p className="mt-2 text-xs text-red-600">
                                    <span className="font-bold">Caution:</span> This electronic acknowledgment confirms mandatory legal compliance.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="p-6 bg-gray-50 flex justify-end space-x-3 border-t border-gray-200">
                    {step > 1 && (
                        <button onClick={() => setStep(step - 1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition shadow-sm">
                            Back
                        </button>
                    )}
                    {step < 3 && (
                        <button 
                            onClick={() => setStep(step + 1)} 
                            disabled={(step === 1 && !isConfirmed) || (step === 2 && (!name || !employeeId))}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition shadow-md ${
                                ((step === 1 && !isConfirmed) || (step === 2 && (!name || !employeeId))) 
                                    ? 'bg-cyan-300 text-white cursor-not-allowed' 
                                    : 'bg-cyan-600 text-white hover:bg-cyan-700'
                            }`}
                        >
                            Continue
                        </button>
                    )}
                    {step === 3 && (
                        <button 
                            onClick={handleFinalAcknowledge} 
                            disabled={signature.length === 0}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition shadow-md ${
                                signature.length === 0
                                    ? 'bg-green-300 text-white cursor-not-allowed' 
                                    : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                        >
                            Acknowledge & Submit
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Content Section Helper ---
const PolicySection = ({ id, title, children }) => (
    <section id={id} className="mb-12 border-b border-gray-200 pb-8">
        <h2 className="text-3xl font-poppins font-bold text-gray-900 mb-6 border-l-4 border-cyan-600 pl-4 py-1 bg-cyan-50 rounded-lg">
            {title}
        </h2>
        <div className="space-y-6">
            {children}
        </div>
    </section>
);


// --- Main App Component ---
const App = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [acknowledgment, setAcknowledgment] = useState(null);

    // Data structure for the navigation and content mapping
    const tabs = useMemo(() => [
        { id: 'definitions', title: '1. Core Definitions & Scope'},
        { id: 'principles', title: '2. Principles of Data Processing'},
        { id: 'consent', title: '3. Consent Requirements'},
        { id: 'rights', title: '4. Rights of the Data Principal'},
        { id: 'fiduciary', title: '5. Obligations of the Data Fiduciary'},
        { id: 'breach', title: '6. Security & Breach Protocol'},
    ], []);

    const baseClasses = "text-base leading-relaxed text-gray-700 font-roboto";
    const ulClasses = "list-disc ml-6 space-y-4 pt-2 font-roboto";
    const liClasses = "p-3 bg-gray-50 rounded-lg border border-gray-100 shadow-sm";

    // Content definitions for each section
    const contentMap = {
        definitions: (
            <div className="space-y-6">
                <p className={baseClasses}>
                    This policy governs the processing of Digital Personal Data in compliance with the Digital Personal Data Protection (DPDP) Act, 2023.
                </p>
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Key Terms</h3>
                <ul className={ulClasses}>
                    <li className={liClasses}>
                        <span className="font-semibold text-cyan-700">Personal Data:</span> Any data about an individual who is identifiable by or in relation to such data.
                    </li>
                    <li className={liClasses}>
                        <span className="font-semibold text-cyan-700">Data Principal:</span> The individual to whom the personal data relates (e.g., employee, customer).
                    </li>
                    <li className={liClasses}>
                        <span className="font-semibold text-cyan-700">Data Fiduciary:</span> The entity (our company) that determines the purpose and means of processing personal data.
                    </li>
                    <li className={liClasses}>
                        <span className="font-semibold text-cyan-700">Processing:</span> Any operation or set of operations performed on personal data, including collection, storage, disclosure, and erasure.
                    </li>
                </ul>
            </div>
        ),
        principles: (
            <div className="space-y-6">
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Seven Core Principles (DPDP Act)</h3>
                <div className="bg-blue-50 p-4 rounded-lg shadow-md border-l-4 border-blue-600 font-roboto">
                    <ul className="list-decimal ml-4 space-y-3 text-gray-700">
                        <li>Processing must be **Lawful, Fair, and Transparent** to the Data Principal.</li>
                        <li>Personal data must be collected only for **specified and lawful purposes**.</li>
                        <li>Only collect data that is **necessary** for the specified purpose (Data Minimisation).</li>
                        <li>Data Fiduciary must ensure the **accuracy** of personal data.</li>
                        <li>Personal data must be stored only for the **period necessary** for the purpose.</li>
                        <li>Personal data must be secured against unauthorized access or processing.</li>
                        <li>The Data Fiduciary must be **Accountable** for compliance with the Act.</li>
                    </ul>
                </div>
            </div>
        ),
        consent: (
            <div className="space-y-6">
                <div className="p-4 bg-yellow-50 border-l-4 border-yellow-600 rounded-lg shadow-sm">
                    <h3 className="font-poppins font-semibold text-yellow-800 mb-1">Valid Consent:</h3>
                    <p className="text-base text-gray-700 font-roboto">
                        Consent must be **free, specific, informed, unconditional, and unambiguous**, and clearly indicate the Data Principal's acceptance (opt-in).
                    </p>
                </div>
                
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Notice Requirements</h3>
                <ul className="space-y-5 pt-2">
                    <li className="p-4 bg-white rounded-xl shadow-lg border-t-4 border-cyan-400">
                        <span className="font-extrabold text-cyan-600 text-lg mr-2">Clear Notice:</span> Before requesting consent, the Data Principal must be provided with a notice describing the personal data to be processed and the purpose of processing.
                    </li>
                    <li className="p-4 bg-white rounded-xl shadow-lg border-t-4 border-cyan-400">
                        <span className="font-extrabold text-cyan-600 text-lg mr-2">Right to Withdraw:</span> The Data Principal has the right to withdraw consent at any time, and the consequences of such withdrawal must be explained in the notice.
                    </li>
                </ul>
            </div>
        ),
        rights: (
            <div className="space-y-6">
                <p className={baseClasses}>
                    The Data Principal is empowered with several rights that the Data Fiduciary must facilitate.
                </p>

                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Facilitated Rights of Data Principal</h3>
                <ul className={ulClasses}>
                    <li className={liClasses}>
                        <span className="font-semibold text-cyan-700">Right to Information:</span> The right to obtain a summary of the personal data being processed and the processing activities.
                    </li>
                    <li className={liClasses}>
                        <span className="font-semibold text-cyan-700">Right to Correction and Erasure:</span> The right to correct inaccurate data and request erasure of data when the purpose is no longer served.
                    </li>
                    <li className={liClasses}>
                        <span className="font-semibold text-cyan-700">Right to Grievance Redressal:</span> The right to have any complaint or concern addressed by the Data Fiduciary.
                    </li>
                </ul>
            </div>
        ),
        fiduciary: (
            <div className="space-y-6">
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Data Fiduciary Responsibilities</h3>
                <p className={baseClasses}>
                    The company (Data Fiduciary) is responsible for ensuring compliance throughout the data lifecycle.
                </p>
                <ul className={ulClasses}>
                    <li className={liClasses}>
                        **Data Quality:** Making reasonable efforts to ensure data is accurate and complete, especially when used to make a decision impacting the Data Principal.
                    </li>
                    <li className={liClasses}>
                        **Retention Limits:** Ceasing to retain personal data as soon as the purpose of processing has been achieved or the Data Principal withdraws consent.
                    </li>
                    <li className={liClasses}>
                        **Grievance Officer:** Designating a Data Protection Officer (or similar Grievance Officer) responsible for responding to Data Principal inquiries and grievances.
                    </li>
                </ul>
            </div>
        ),
        breach: (
            <div className="space-y-6">
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Data Security Measures</h3>
                <p className={baseClasses}>
                    We implement reasonable security safeguards to prevent personal data breaches, including technical and organizational measures proportionate to the risk.
                </p>
                
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Personal Data Breach Protocol</h3>
                <div className="bg-red-50 p-4 rounded-lg shadow-md border-l-4 border-red-600 font-roboto">
                    <h4 className="font-poppins font-semibold text-red-700 mb-2">Mandatory Notification:</h4>
                    <ul className="list-disc ml-4 space-y-2 text-sm text-gray-700">
                        <li>In the event of a personal data breach, the company must notify the Data Protection Board of India.</li>
                        <li>The affected Data Principals must also be notified immediately, detailing the nature of the breach, the data involved, and the remedial actions taken.</li>
                    </ul>
                </div>
                
                <h3 className="text-xl font-poppins font-bold text-gray-800 pt-2">Grievance Redressal</h3>
                <p className={baseClasses}>
                    Any Data Principal who believes their data rights have been violated may submit a complaint to the designated Grievance Officer. The officer is mandated to resolve the grievance within a reasonable timeframe (typically 30 days).
                </p>
            </div>
        )
    };

    return (
        <>
            <style>
                {/* CSS and Font Definitions (kept outside JSX for cleaner syntax) */}
                {`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Roboto:wght@400;500;700&display=swap');
                .font-poppins { font-family: 'Poppins', sans-serif; }
                .font-roboto { font-family: 'Roboto', sans-serif; }
                .shadow-4xl {
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
                }
                `}
            </style>
            
            {/* Minimal Main Container for Plain Content */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 font-roboto bg-white">
                
                {/* Header Section - Plain Style */}
                <header className="py-8 border-b border-gray-300 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                    <div className="flex items-center mb-3 sm:mb-0">
                        <svg className="w-8 h-8 sm:w-10 sm:h-10 mr-4 text-cyan-700 p-1 bg-cyan-100 rounded-full flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.618a8.25 8.25 0 10-1.2 11.237l4.004 4.003a1 1 0 001.414-1.414l-4.003-4.004a8.25 8.25 0 00-11.237-1.2zM12 18a6 6 0 110-12 6 6 0 010 12z"></path></svg>
                        <div>
                            <h1 className="text-xl sm:text-3xl font-poppins font-extrabold text-gray-900">
                                Data Protection & DPDP Act Compliance Policy
                            </h1>
                            <p className="text-xs sm:text-sm text-gray-500 font-roboto">
                                Adhering to the Digital Personal Data Protection Act, 2023 (India)
                            </p>
                        </div>
                    </div>

                    {/* Acknowledge Button */}
                    {acknowledgment ? (
                        <div className="text-sm font-medium text-green-700 bg-green-100 p-2.5 rounded-xl border border-green-200 mt-4 sm:mt-0">
                            Acknowledged by: {acknowledgment.signature} on {acknowledgment.date}
                        </div>
                    ) : (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="w-full sm:w-auto mt-4 sm:mt-0 px-5 py-2.5 bg-cyan-600 text-white font-poppins font-semibold text-sm rounded-xl shadow-lg hover:bg-cyan-700 transition transform focus:outline-none focus:ring-4 focus:ring-cyan-500 focus:ring-opacity-50"
                        >
                            Acknowledge Policy
                        </button>
                    )}
                </header>
                
                {/* Content Table of Contents/Navigation Links */}
                <nav className="my-6 sticky top-0 bg-white pt-2 pb-4 z-10 border-b border-gray-100">
                    <h3 className="text-lg font-poppins font-semibold text-gray-800 mb-3">Policy Sections:</h3>
                    <div className="flex flex-wrap gap-2">
                        {tabs.map(tab => (
                            <a 
                                key={tab.id} 
                                href={`#${tab.id}`}
                                className="px-4 py-2 text-sm font-medium rounded-full bg-cyan-50 text-cyan-700 hover:bg-cyan-100 transition shadow-sm"
                            >
                                {tab.title}
                            </a>
                        ))}
                    </div>
                </nav>

                {/* Policy Content Area - Sequential Flow */}
                <main className="pt-4">
                    {Object.entries(contentMap).map(([id, content], index) => (
                        <PolicySection key={id} id={id} title={tabs[index].title}>
                            {content}
                        </PolicySection>
                    ))}
                </main>
            </div>

            {/* Conditional Modal Render */}
            {isModalOpen && <DPDPAcknowledgeModal 
                onClose={() => setIsModalOpen(false)} 
                onAcknowledge={setAcknowledgment} 
            />}
        </>
    );
};

export default App;